import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Menu,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/system';
import CustomBox from '../../components/Dashboard/CustomBox/CustomBox';
import ButtonPending from '../../components/Dashboard/DashboardButton/ButtonPending';
import ButtonAccept from '../../components/Dashboard/DashboardButton/ButtonAccept';
import ButtonCancel from '../../components/Dashboard/DashboardButton/ButtonCancel';
import ButtonUnComplete from '../../components/Dashboard/DashboardButton/ButtonUnComplete';
import ButtonIdentify from '../../components/Dashboard/DashboardButton/ButtonIdentify';
import useIdentities from '../../hooks/useIdentities';
import { CardListSkeleton } from '../../components/Feedback/Loaders';
import { SPACING, outlinedButtonSx } from '../../theme/theme';
import Breadcrumb from '../../components/Navigation/Breadcrumb';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useQueryClient } from '@tanstack/react-query';
import WarningIcon from '@mui/icons-material/Warning';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PlusIcon from '/src/assets/images/button/plus-dashboard.svg?url';
import { resetForm, setCurrentStep } from 'Slice/unifiedFormSlice';
import AddAgentModal from '../../components/Dashboard/Reseller/AddAgentModal/AddAgentModal';
import { revokePublisher, getAgentPublishers, removeAgent } from '../../Slice/reseller/agent/agentSlice';
import { Representative } from '../../components/Dashboard/RepresentativeCard/RepresentativeCard';
import { toast } from 'react-toastify';

const PageContainer = styled(Box)({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginTop: '0px',
  padding: '40px',
  backgroundColor: 'inherit',
});

const ContentWrapper = styled(Paper)({
  width: '950px',
  minHeight: '400px',
  padding: '0px',
  boxShadow: 'none',
  backgroundColor: 'inherit',
  direction: 'rtl',
});

const NavbarContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: '950px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0 20px',
  borderRadius: '8px',
  marginBottom: '20px',
  '@media (max-width: 768px)': {
    width: '100%',
    padding: '0 10px',
  },
}));

const StyledButton = styled(Box)(() => ({
  fontFamily: 'IRANYekanXFaNum',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '16px',
  textTransform: 'none',
  width: '130px',
  height: '40px',
  borderRadius: '8px',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '2px',
  ...outlinedButtonSx,
  '@media (max-width: 768px)': {
    width: 'auto',
    minWidth: '130px',
    fontSize: '12px',
    padding: '8px 12px',
    whiteSpace: 'nowrap',
  },
}));

const DraftIdentityContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
  // Takes the space the buttons leave instead of a fixed 400px, which forced
  // the message onto two lines while the middle of the row sat empty.
  flex: '1 1 auto',
  minWidth: 0,
  maxWidth: '620px',
  padding: '8px 12px',
  borderRadius: '12px',
  backgroundColor: '#FFF8E1',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#FFE082',
  },
  '@media (max-width: 768px)': {
    width: '100%',
    maxWidth: 'none',
    padding: '8px',
  },
}));

const DraftIdentityText = styled(Typography)(() => ({
  fontFamily: 'IRANYekanXFaNum',
  fontSize: '12px',
  fontWeight: 500,
  color: '#25282E',
  textAlign: 'right',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'normal',
}));

const DraftIdentityIcon = styled(WarningIcon)(() => ({
  borderRadius: '4px',
  width: '28px',
  height: '28px',
  color: '#FF6F00',
}));

const IdentitiesRow = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row-reverse',
  justifyContent: 'space-between',
  // Without this the row stretches every child to the tallest one, so the
  // buttons grew to match the two-line warning and stopped lining up with it.
  alignItems: 'center',
  width: '100%',
  maxWidth: '950px',
  gap: '8px',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
  },
}));

interface LocationState {
  id?: string;
  display_name?: string;
  refreshKey?: number;
}

const Identifies: React.FC = () => {
  const {
    data: identities,
    isLoading,
    isError,
    error,
    refetch,
  } = useIdentities();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryClient = useQueryClient();
  const currentStep = useSelector(
    (state: RootState) => state.unifiedForm.currentStep,
  );
  const isParent = useSelector((state: RootState) => state.auth.isParent);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);

  const locationState = location.state as LocationState | undefined;

  const draftIdentities = identities?.filter(
    (identity) => identity.content_status === 'DRAFT',
  );

  const handleMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (draftIdentities && draftIdentities.length > 0) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleIdentityClick = (identity: any) => {
    const nextStep = parseInt(identity.next_step) || 2;
    navigate('/wizard', {
      state: {
        id: identity.id,
        name: identity.name,
        display_name: identity.display_name,
        identity_type: identity.identity_type,
        nextStep: nextStep,
        mode: 'new',
      },
    });
    handleMenuClose();
  };

  const handleNewIdentityClick = () => {
    // Clear the whole form, not just the step: Wizard falls back to
    // `identity.id` from Redux when location.state carries no id, so a
    // previously selected identity would send it straight to ثبت اپلیکیشن.
    dispatch(resetForm());
    dispatch(setCurrentStep(1));
    navigate('/wizard', { state: { mode: 'new' } });
  };


  const handleUnCompleteClick = (identity: any) => {
    const nextStep = parseInt(identity.next_step) || 2;
    navigate('/wizard', {
      state: {
        id: identity.id,
        name: identity.name,
        display_name: identity.display_name,
        identity_type: identity.identity_type,
        nextStep: nextStep,
        mode: 'dashboard',
      },
    });
  };

  useEffect(() => {
    if (locationState?.refreshKey) {
      console.log('Refreshing data due to navigation...');
      // استفاده از setTimeout برای جلوگیری از race condition
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['identities'] });
        refetch();
      }, 100);

      navigate(location.pathname, {
        replace: true,
        state: {
          id: locationState?.id,
          display_name: locationState?.display_name,
        },
      });
    }
  }, [locationState, queryClient, refetch, navigate, location.pathname]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('refresh') === 'true') {
      console.log('Invalidating identities query from URL...');
      // استفاده از setTimeout برای جلوگیری از race condition
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['identities'] });
        refetch();
      }, 100);
    }
  }, [location.search, queryClient, refetch]);

  const handleIdentifyClick = (id: string) => {
    navigate(`/dashboard/identify/${id}`);
  };

  const handleRevokeAccess = async (rep: Representative, identityId: number) => {
    if (!rep.id) {
      toast.error('شناسه نماینده موجود نیست');
      return;
    }

    try {
      const result: any = await (dispatch as any)(
        revokePublisher({
          child_user_id: rep.id,
          publisher_id: identityId,
        })
      );

      if (revokePublisher.fulfilled.match(result)) {
        // Update localStorage to remove the representative
        const storedRepresentatives = localStorage.getItem('identity_representatives');
        const representativesMap = storedRepresentatives
          ? JSON.parse(storedRepresentatives)
          : {};

        if (representativesMap[identityId]) {
          representativesMap[identityId] = representativesMap[identityId].filter(
            (r: any) => r.id !== rep.id
          );
          localStorage.setItem(
            'identity_representatives',
            JSON.stringify(representativesMap)
          );
        }

        toast.success('دسترسی نماینده با موفقیت حذف شد');

        // Refetch identities to update UI
        refetch();
      } else {
        toast.error('خطا در حذف دسترسی نماینده');
      }
    } catch {
      toast.error('خطا در حذف دسترسی نماینده');
    }
  };

  const handleRemoveRepresentative = async (rep: Representative, _identityId: number) => {
    if (!rep.id) {
      toast.error('شناسه نماینده موجود نیست');
      return;
    }

    // نمایش الرت تاییدی
    const confirmed = window.confirm(
      `با حذف نماینده "${rep.name || rep.mobile}"، دسترسی او به همه هویت‌ها گرفته می‌شود.\n\nآیا مطمئن هستید؟`
    );

    if (!confirmed) {
      return;
    }

    try {
      // مرحله 1: دریافت لیست همه هویت‌هایی که نماینده به آنها دسترسی دارد
      const publishersResult: any = await (dispatch as any)(
        getAgentPublishers({
          child_user_id: rep.id,
        })
      ).unwrap();

      // مرحله 2: حذف دسترسی از همه هویت‌ها
      for (const publisher of publishersResult) {
        await (dispatch as any)(
          revokePublisher({
            child_user_id: rep.id,
            publisher_id: publisher.id,
          })
        ).unwrap();
      }

      // مرحله 3: حذف کامل نماینده
      await (dispatch as any)(
        removeAgent({
          child_user_id: rep.id,
        })
      ).unwrap();

      // مرحله 4: حذف از localStorage
      const storedRepresentatives = localStorage.getItem('identity_representatives');
      const representativesMap = storedRepresentatives
        ? JSON.parse(storedRepresentatives)
        : {};

      // حذف نماینده از همه هویت‌ها در localStorage
      Object.keys(representativesMap).forEach((key) => {
        representativesMap[key] = representativesMap[key].filter(
          (r: any) => r.id !== rep.id
        );
      });

      localStorage.setItem(
        'identity_representatives',
        JSON.stringify(representativesMap)
      );

      toast.success('نماینده با موفقیت حذف شد');

      // مرحله 5: بروزرسانی UI
      refetch();
    } catch (error) {
      console.error('Error removing representative:', error);
      toast.error('خطا در حذف نماینده');
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <ContentWrapper>
          {/* Skeleton rather than a spinner: the result is a known list of
              cards, so this holds the layout instead of shifting it. */}
          <CardListSkeleton count={3} height={140} />
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography variant="h6" color="error">
              خطا در بارگذاری
            </Typography>
            <Typography
              variant="body2"
              color="error"
              sx={{ textAlign: 'center' }}
            >
              {error?.message || 'خطا در دریافت اطلاعات هویت‌ها'}
            </Typography>
            <Button variant="outlined" onClick={() => refetch()} sx={{ mt: 2 }}>
              تلاش مجدد
            </Button>
          </Box>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (!identities || identities.length === 0) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Typography variant="body1">هویتی یافت نشد.</Typography>
          <StyledButton onClick={handleNewIdentityClick}>
            <img
              src={PlusIcon}
              alt="icon"
              style={{ width: '20px', height: '20px' }}
            />
            <Typography
              sx={{
                fontFamily: 'IRANYekanXFaNum',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '16px',
                color: 'inherit',
              }}
            >
              هویت جدید
            </Typography>
          </StyledButton>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <Breadcrumb
          items={[
            { label: 'داشبورد', to: '/dashboard' },
            { label: 'هویت‌ها' },
          ]}
        />
        <NavbarContainer style={{marginTop: '30px',
            padding:'10px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 2px 10px rgba(16, 24, 40, 0.06)'}}>
          <IdentitiesRow>
            <DraftIdentityContainer onClick={handleMenuClick}>
              <DraftIdentityText>
                {isLoading ? (
                  // Matches IdentitiesNavbar: inline spinner inside the pill.
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <CircularProgress size={14} thickness={5} />
                    در حال بارگذاری
                  </Box>
                ) : isError ? (
                  'خطا در بارگذاری!'
                ) : draftIdentities && draftIdentities.length > 0 ? (
                  'توسعه دهنده عزیز شما فرایند ثبت اطلاعاتی هویت خود را کامل نکرده اید و برای تکمیل آن کلیک کنید'
                ) : (
                  'هویت جدید'
                )}
              </DraftIdentityText>
              <DraftIdentityIcon />
            </DraftIdentityContainer>

            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                // The buttons keep their width when the warning text grows.
                flexShrink: 0,
              }}
            >
              {isParent && (
                <StyledButton onClick={() => setIsAgentModalOpen(true)}>
                  <PersonAddIcon
                    sx={{ width: '20px', height: '20px', color: 'inherit' }}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'IRANYekanXFaNum',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '16px',
                      color: 'inherit',
                    }}
                  >
                    ایجاد نماینده
                  </Typography>
                </StyledButton>
              )}

              <StyledButton onClick={handleNewIdentityClick}>
                {/* The button's own styles tint the icon and colour the label,
                    and flip both to white on hover; an inline style would
                    outrank that. */}
                <img
                  src={PlusIcon}
                  alt="icon"
                  style={{ width: '20px', height: '20px' }}
                />
                <Typography
                  sx={{
                    fontFamily: 'IRANYekanXFaNum',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '16px',
                    color: 'inherit',
                  }}
                >
                  هویت جدید
                </Typography>
              </StyledButton>
            </Box>
          </IdentitiesRow>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              style: {
                maxHeight: '300px',
                width: '620px',
                direction: 'rtl',
              },
            }}
          >
            {draftIdentities && draftIdentities.length > 0 ? (
              draftIdentities.map((identity) => (
                <MenuItem
                  key={identity.id}
                  onClick={() => handleIdentityClick(identity)}
                  style={{ direction: 'rtl' }}
                >
                  <Typography
                    style={{
                      fontFamily: 'IRANYekanXFaNum',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '16px',
                      color: '#000000',
                      textAlign: 'right',
                      width: '100%',
                    }}
                  >
                    توسعه دهنده {identity.name} هویت {identity.display_name}{' '}
                    اطلاعات آن را کامل نکرده‌اید
                  </Typography>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled style={{ direction: 'rtl' }}>
                <Typography
                  style={{
                    fontFamily: 'IRANYekanXFaNum',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '16px',
                    color: '#000000',
                    textAlign: 'right',
                    width: '100%',
                  }}
                >
                  هویت ناکامل دیگری وجود ندارد
                </Typography>
              </MenuItem>
            )}
          </Menu>
        </NavbarContainer>

        {locationState?.id && (
          <Box sx={{ mb: 2, textAlign: 'right', padding: '0 16px' }}>
            <Typography variant="subtitle2">
              اطلاعات دریافتی: {locationState.display_name} - {locationState.id}
            </Typography>
          </Box>
        )}

        {/* Pixel value — the theme sets `spacing: 1`. Was a 2px gutter. */}
        <Grid container spacing={SPACING.md}>
          {identities.map((identity) => {
            let actionButton;
            let identityButton;

            switch (identity.content_status) {
              case 'DRAFT':
                actionButton = (
                  <Box
                    onClick={() => handleUnCompleteClick(identity)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ButtonUnComplete />
                  </Box>
                );
                identityButton = (
                  <Box
                    onClick={() => handleIdentifyClick(identity.id.toString())}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ButtonIdentify />
                  </Box>
                );
                break;
              case 'PENDING':
                actionButton = <ButtonPending />;
                identityButton = (
                  <Box
                    onClick={() => handleIdentifyClick(identity.id.toString())}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ButtonIdentify />
                  </Box>
                );
                break;
              case 'APPROVED':
                actionButton = <ButtonAccept />;
                identityButton = (
                  <Box
                    onClick={() => handleIdentifyClick(identity.id.toString())}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ButtonIdentify />
                  </Box>
                );
                break;
              case 'REJECTED':
                actionButton = <ButtonCancel />;
                identityButton = (
                  <Box
                    onClick={() => handleIdentifyClick(identity.id.toString())}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ButtonIdentify />
                  </Box>
                );
                break;
              default:
                actionButton = null;
                identityButton = (
                  <Box
                    onClick={() => handleIdentifyClick(identity.id.toString())}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ButtonIdentify />
                  </Box>
                );
            }

            // نمایندگان از API دریافت می‌شوند
            const representatives = identity.representatives || identity.agents || [];

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                key={identity.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  p: `${SPACING.sm}px`,
                }}
              >
                <CustomBox
                  content_status={identity.content_status}
                  name={identity.name}
                  display_name={identity.display_name}
                  wallets_count={Number(identity.wallets_count)}
                  published_applications_count={Number(
                    identity.published_applications_count,
                  )}
                  sibche_address={identity.sibche_address}
                  applications_count={Number(identity.applications_count)}
                  publish_status={identity.publish_status}
                  identityStatus={identity.content_status}
                  actionButton={actionButton}
                  identityButton={identityButton}
                  statusColor={''}
                  statusBgColor={''}
                  identity_type={identity.identity_type}
                  identity_id={identity.id}
                  representatives={representatives}
                  onRevokeAccess={(rep) => handleRevokeAccess(rep, identity.id)}
                  onRemoveRepresentative={(rep) => handleRemoveRepresentative(rep, identity.id)}
                />
              </Grid>
            );
          })}
        </Grid>
      </ContentWrapper>

      <AddAgentModal
        open={isAgentModalOpen}
        onClose={() => setIsAgentModalOpen(false)}
        onSuccess={() => {
          // Refetch identities to update the list with new representative
          refetch();
        }}
      />
    </PageContainer>
  );
};

export default Identifies;
